import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowUUpLeft, Copy, Info, Lock, Pause, PencilSimple, Play, Rows, Trash, WebhooksLogo} from '../../icons';
import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {DetailTop} from '../DetailTop';
import {RowMenu} from '../rules/RowMenu';
import {ruleBadge} from '../rules/RuleRow';
import {isOk} from '../../../utils/apiResponse';
import {AccessService} from '../../../utils/AccessService';
import {AlertRulesApi} from '../../../api/alertRulesApi';
import {useUserModules} from '../../../context/UserModulesContext';
import {SentenceChips} from './SentenceChips';
import {SimulationResult} from './SimulationResult';
import {NaturalLanguageDraft} from './NaturalLanguageDraft';
import {chooseScopeOption, liveMarks, stampMarks} from '../naturalLanguageModel';
import {alertTitle, controlScopeText, exceptionTitle, metricLabel, periodLabel} from '../ruleText';
import {
    availableChannels,
    availableTemplates,
    CHANNEL_EMAIL,
    CHANNEL_WEBHOOK,
    CONDITION_CONTROL,
    CONDITION_PERIOD_CHANGE,
    draftChanges,
    draftFromRule,
    copyDraftName,
    copyFollowsSentence,
    duplicateDraft,
    emptyDraft,
    isExceptionRule,
    ruleFromResponse,
    teamActionOf,
    withViewerOptOut,
    draftWithViewerExcluded,
    exceptNames,
    ERROR_LIMIT_REACHED,
    ERROR_NOT_FOUND,
    errorFromResponse,
    hiddenScopeCount,
    isActiveRule,
    KIND_SYSTEM,
    limitReached,
    NAME_MAX_LENGTH,
    needsCurrencyNote,
    PAUSED_CREATOR_LEFT,
    PAUSED_LIMIT,
    proposedName,
    recipientUsersFromOptions,
    ruleInputFromDraft,
    ruleUpdateFromDraft,
    scopeNamesFromOptions,
    scopeNamesFromRule,
    sentenceTokens,
    staleRecipientIds,
    staleScopeIds,
    templateDraft,
    unitOfMetric,
    validateDraft,
    parseValue,
    valueUnitOf,
    withEmailAlways,
    withOnlyMe,
} from '../rulesModel';
import {PARAM_DUPLICATE, PARAM_PAUSE} from '../alertsCenterPaths';

export const MODE_NEW = 'new';
export const MODE_EDIT = 'edit';

const TAB_STEPS = 'steps';
// Lo que, abierto, se queda con un Escape que llega a la página: un chip o un «⋯» abiertos, un panel (el del webhook, un
// modal, una guía) o un desplegable del marco. Por su marca de abierto: un `[role="dialog"]` a secas no sirve, porque la
// campana del topbar deja el suyo montado siempre.
const PAGE_ESCAPE_OWNERS = '.alc-chip-menu, .alc-mr-menu, .alc-drawer, [aria-modal="true"], .guide-card, '
    + '.topbar-dropdown.show, .nbell-panel.show, .sidebar-v2-agency-trigger[aria-expanded="true"]';
const TAB_TEXT = 'text';

// Qué errores del borrador corresponden a cada bloque del PUT: editando, sólo se valida lo que viaja.
const FIELDS_BY_BLOCK = {
    name: ['name'],
    condition: ['metric', 'operator', 'value', 'period', 'vsPeriod'],
    scope: ['scope'],
    delivery: ['channels', 'recipients'],
};

/**
 * El constructor de una regla (`/alerts/new`, `/alerts/rules/<id>`): la frase con chips, el nombre
 * que se propone solo, «Simular» y «Guardar».
 *
 * - Crear, editar y duplicar son el mismo borrador (`rulesModel`). Duplicar llega por
 *   `?duplicar=<id>`: sobrevive a un F5.
 * - Editar manda SÓLO lo que cambió (C-ac-fe-centro-01): un bloque que no se tocó no se revalida ni se
 *   reemplaza. Sin cambios, no hay nada que guardar.
 * - Un alcance con ids que quien mira no ve (`hidden_count`) se nombra en la frase y no se edita: sólo
 *   lo cambia quien lo ve entero. Los ids guardados que ya no existen (una línea terminada, alguien que
 *   se fue) se señalan: guardarlos de nuevo da `INVALID_SCOPE` / `INVALID_RECIPIENTS`.
 * - Los errores del backend se muestran por `error_code` con texto propio y marcan su chip; nunca el
 *   `error_message` crudo. El tope de reglas activas deshabilita «Guardar» y frena «Reanudar».
 * - Editando: pausar/reanudar, duplicar y eliminar (con «Deshacer» en la lista). `?pausar=1` (el link
 *   «Pausar esta regla» del correo) pide confirmar en pantalla.
 * - `canEdit` en false: la regla se lee y no se cambia.
 * - «En tus palabras» (F4) sólo al crear: la frase arma el MISMO borrador, con los chips que armó la IA
 *   resaltados y los dudosos vacíos (`marks`). Lo que el usuario corrige deja de estar marcado. Editar
 *   una regla es corregir su frase, no reescribirla: ahí no hay solapas.
 * - Al crear «Paso a paso», arriba de la frase, plantillas sugeridas (C-06, 13/10): un clic arma la frase
 *   sin el valor y deja el cursor en el valor. No guarda nada: el usuario completa y guarda.
 * - v2: el operador elige también «sube / cae / se aleja más de» contra otro período (`PERIOD_CHANGE`), la
 *   métrica ofrece «De la línea» (período fijo) y, si `options` los trae, «toda la agencia» y «los
 *   administradores». Una de Dashbo convertida no se edita acá (su número va en la fila): `onSystemRule`
 *   lleva a su fila.
 */
/**
 * Los nombres de las bajas elegidas («salvo …», ronda 5): de las personas del equipo (`options.users`) o, si no están ahí,
 * de lo que el backend resolvió en la regla (`recipients.excluded`). Quien mira es «tú» y va primero, como en la fila
 * (decía su nombre: «salvo Fede y 3 más» en el editor y «salvo tú» en la lista).
 */
function excludedNamesOf(recipients, options, rule, {t, viewerId}) {
    const ids = (recipients && recipients.excludedUserIds) || [];
    if (!ids.length) return [];
    const fromRule = (rule && rule.delivery && rule.delivery.recipients && rule.delivery.recipients.excluded) || [];
    const byId = new Map([...fromRule, ...((options && options.users) || [])]
        .map((user) => [String(user.id), user.name || user.email]).filter(([, name]) => name));
    return exceptNames(ids, byId, {t, viewerId}).names;
}

export function RuleBuilder({mode, ruleId, options, optionsState, onRetryOptions, limits, canEdit = true,
                                onSaved, onDeleted, onDuplicate, onRuleChanged, onGone, onSystemRule = null,
                                allowNoChannels = false, onlyMe = false, emailAlways = false, canExclude = false,
                                onCancel = null, viewerId = null}) {
    const {t, i18n} = useTranslation();
    const locale = i18n.language;
    const [searchParams, setSearchParams] = useSearchParams();
    const duplicateId = mode === MODE_NEW ? searchParams.get(PARAM_DUPLICATE) : null;
    const askPause = mode === MODE_EDIT && searchParams.get(PARAM_PAUSE) === '1';

    const [rule, setRule] = useState(null);
    // Ronda 4 (D13): en una regla que se lee y no se cambia (`can_edit` en false: las de la agencia para un no admin),
    // no se guarda; pausar y borrar siguen a `can_pause` / `can_delete` (el creador de una propia vieja que avisa a
    // otros puede las dos cosas).
    const editable = canEdit && !(mode === MODE_EDIT && rule && rule.can_edit === false);
    const canPauseRule = canEdit && Boolean(rule) && (typeof rule.can_pause === 'boolean' ? rule.can_pause : editable);
    const canDeleteRule = canEdit && Boolean(rule) && (typeof rule.can_delete === 'boolean' ? rule.can_delete : editable);
    const [sourceState, setSourceState] = useState(mode === MODE_EDIT || duplicateId ? 'loading' : 'ok');
    const [rawDraft, setDraft] = useState(emptyDraft);
    // Un no admin crea sólo «a mí» (D13): al crear, el borrador va siempre con ese destinatario, también el de una copia
    // o el que arma «En tus palabras». Una regla que ya existe se lee con sus destinatarios de verdad: una ajena que le
    // llega, o una propia vieja que avisa a otros, no dice «a mí» (review P-av2-fe-centro-01). En una suya que puede
    // editar (las suyas son «a mí»), el chip igual no se elige.
    const forMe = onlyMe && mode === MODE_NEW ? withOnlyMe(rawDraft) : rawDraft;
    // En una agencia convertida el correo va siempre (ronda 5, D18): el webhook se suma con «También al webhook». Salvo
    // en un aviso que no sale por correo (el saldo de un backend anterior): ahí el correo daría un 422.
    const emailForced = emailAlways && (!rule || availableChannels(rule).includes(CHANNEL_EMAIL));
    const draft = emailForced ? withEmailAlways(forMe) : forMe;
    // El chip «Webhook» de la frase (UD-18; antes, el switch «También al webhook» de la ronda 5): en una agencia
    // convertida, si la agencia tiene el webhook y el aviso lo admite («inversión sin control» sale sólo por correo). Sin
    // la dirección conectada se ve, pero no se prende.
    const webhookInfo = (options && options.channels && options.channels.webhook) || null;
    const webhookOffered = emailForced && (!webhookInfo || webhookInfo.available !== false)
        && (!rule || availableChannels(rule).includes(CHANNEL_WEBHOOK));
    const webhookReady = !webhookInfo || webhookInfo.configured !== false;
    const lockRecipients = onlyMe && (mode === MODE_NEW || editable);
    const [marks, setMarks] = useState({});
    const [tab, setTab] = useState(TAB_STEPS);
    const {modules} = useUserModules();
    const hasNaturalLanguage = Array.isArray(modules) && modules.includes(AccessService.MODULE_DASHBO_MCP);
    const [showErrors, setShowErrors] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [simulation, setSimulation] = useState(null);
    const [simulating, setSimulating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [acting, setActing] = useState(false);
    // «¿Descartas los cambios?» (UD-18): «Cancelar» o Escape con cambios sin guardar preguntan en el lugar.
    const [confirming, setConfirming] = useState(false);
    const confirmRef = useRef(null);
    // El borrador con el que arrancó la pantalla (vacío, el de una copia): una regla nueva tiene cambios si se apartó.
    const startDraft = useRef(JSON.stringify(emptyDraft()));
    // Lo escrito en «En tus palabras» todavía sin convertir: no está en el borrador, pero también se perdería al salir
    // (review C-ud18-fe-02).
    const [wordsTyped, setWordsTyped] = useState(false);
    // Cada plantilla elegida suma uno: el efecto lleva el foco al valor, que es lo que falta completar.
    const [focusValue, setFocusValue] = useState(0);
    // El borrador que armó la última plantilla: mientras siga igual, elegir otra no pisa nada del usuario.
    const [templateBase, setTemplateBase] = useState(null);
    // La plantilla elegida sobre una frase con trabajo del usuario: espera que confirme (review C-02).
    const [pendingTemplate, setPendingTemplate] = useState(null);
    const mounted = useRef(true);
    const rootRef = useRef(null);
    const templateConfirmRef = useRef(null);
    // Cada cambio de la frase y cada «Simular» suman uno: una respuesta que llega tarde (de otra frase) se
    // descarta en vez de pintarse debajo de la nueva (review C-03).
    const simulationRequest = useRef(0);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        if (!focusValue || !rootRef.current) return;
        const input = rootRef.current.querySelector('[data-slot="value"] input');
        if (input) input.focus();
    }, [focusValue]);

    // El «¿Reemplazar la frase?» se enfoca al aparecer: es un `alertdialog`, como el de pausar.
    useEffect(() => {
        if (!pendingTemplate || !templateConfirmRef.current) return;
        const button = templateConfirmRef.current.querySelector('button');
        if (button) button.focus();
    }, [pendingTemplate]);

    // La regla a editar o a duplicar. Una respuesta de una regla anterior (otra pestaña de la misma
    // ruta) se descarta.
    const sourceId = mode === MODE_EDIT ? ruleId : duplicateId;
    useEffect(() => {
        // Otro borrador de base: las marcas de una frase anterior ya no describen nada.
        setMarks({});
        setConfirming(false);
        setWordsTyped(false);
        if (!sourceId) {
            setRule(null);
            setDraft(emptyDraft());
            startDraft.current = JSON.stringify(emptyDraft());
            setSourceState('ok');
            return undefined;
        }
        let cancelled = false;
        setSourceState('loading');
        AlertRulesApi.get(sourceId).then((response) => {
            if (cancelled) return;
            const loaded = isOk(response) && response.result ? response.result.rule : null;
            if (!loaded) {
                setSourceState('error');
                return;
            }
            // Una de Dashbo se edita acá (ronda 5, D20). El link «¿pausar?» de un correo la lleva a su fila, que ofrece
            // además dejar de recibirla.
            if (mode === MODE_EDIT && loaded.kind === KIND_SYSTEM && askPause && onSystemRule) {
                onSystemRule(loaded, {pause: true});
                return;
            }
            setRule(mode === MODE_EDIT ? loaded : null);
            const loadedDraft = mode === MODE_EDIT
                ? draftFromRule(loaded, {locale})
                : duplicateDraft(loaded, t('alerts_center.builder.copy_suffix'), {locale});
            setDraft(loadedDraft);
            startDraft.current = JSON.stringify(loadedDraft);
            setSourceState('ok');
        });
        return () => { cancelled = true; };
        // `t` cambia de identidad entre renders de i18next; la regla sólo depende del id.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, sourceId]);

    const hiddenCount = mode === MODE_EDIT ? hiddenScopeCount(rule) : 0;
    const unit = valueUnitOf(draft, options);
    const ctx = useMemo(() => {
        const sameScopeAsSaved = rule && draft.scope.type === (rule.scope && rule.scope.type)
            && String(draft.scope.ids) === String(rule.scope.ids);
        const fromRule = sameScopeAsSaved ? scopeNamesFromRule(rule) : [];
        const fromOptions = scopeNamesFromOptions(draft.scope, options);
        return {
            t,
            locale,
            unit,
            metricLabel: (metric) => metricLabel(metric, {
                options,
                fallbackLabel: rule && rule.condition && rule.condition.metric === metric ? rule.condition.metric_label : null,
                t,
            }),
            periodLabel: (period) => periodLabel(period, t),
            scopeNames: fromOptions.length ? fromOptions : fromRule,
            hiddenCount: sameScopeAsSaved ? hiddenCount : 0,
            recipientNames: recipientUsersFromOptions(draft.recipients, options),
            allowNoChannels,
            emailAlways: emailForced,
            canExclude,
            excludedNames: excludedNamesOf(draft.recipients, options, rule, {t, viewerId}),
            // Un aviso de control: su nombre y dónde mira, fijos (ronda 5).
            controlLabel: rule ? alertTitle(rule, t) : '',
            controlScope: rule ? controlScopeText(rule, t) : null,
            // El chip «Webhook» de la frase (UD-18): si se ofrece y si se puede prender.
            webhookOffered,
            webhookReady,
            editable,
        };
    }, [t, locale, unit, options, rule, draft.scope, draft.recipients, hiddenCount, allowNoChannels, emailForced, canExclude,
        webhookOffered, webhookReady, editable, viewerId]);

    const tokens = sentenceTokens(draft, ctx);
    // Lo que el usuario corrigió a mano deja de estar «interpretado» o «en duda».
    const shownMarks = liveMarks(marks, draft);
    // Una copia: su nombre sigue a la frase si el de la original era el automático; si era escrito a
    // mano, se conserva con «(copia)» (C-ac-fe-centro-27). La original se lee con SU alcance.
    const originalCtx = draft.copyOf ? {
        ...ctx,
        unit: valueUnitOf(draft.copyOf.draft, options),
        scopeNames: scopeNamesFromOptions(draft.copyOf.draft.scope, options),
    } : null;
    const autoName = proposedName(draft, ctx);
    const suggestedName = draft.copyOf
        ? copyDraftName(draft.copyOf, autoName, originalCtx, t('alerts_center.builder.copy_suffix')) : autoName;
    const nameFollowsSentence = !draft.copyOf || copyFollowsSentence(draft.copyOf, originalCtx);
    const effectiveName = draft.nameTouched ? draft.name : suggestedName;
    const finalDraft = {...draft, name: effectiveName};
    const update = useMemo(() => (mode === MODE_EDIT && rule
        ? ruleUpdateFromDraft(rule, finalDraft, {locale}) : null),
    // `finalDraft` se arma en cada render con `draft` y el nombre: dependen de eso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, rule, draft, effectiveName, locale]);
    const nothingToSave = mode === MODE_EDIT && update !== null && Object.keys(update).length === 0;

    // Una de Dashbo no tiene nombre propio (se titula por su clave): no se pide.
    let localErrors = validateDraft(finalDraft, {requireName: !draft.systemLocked, locale, limits, allowNoChannels});
    if (update) {
        // Editando, lo que no viaja no se valida: un alcance con una línea que terminó no impide
        // cambiar el umbral.
        const sent = new Set(Object.keys(update).flatMap((block) => FIELDS_BY_BLOCK[block] || []));
        localErrors = Object.fromEntries(Object.entries(localErrors).filter(([field]) => sent.has(field)));
    }
    const errors = showErrors ? {...localErrors} : {};
    if (serverError && serverError.field) errors[serverError.field] = serverError.key;

    const staleScope = mode === MODE_EDIT ? staleScopeIds(draft.scope, options) : [];
    const staleRecipients = mode === MODE_EDIT ? staleRecipientIds(draft.recipients, options) : [];

    // El tope: crear (o duplicar) una regla activa, o reanudar una pausada al editarla.
    const wouldAddActive = mode === MODE_NEW || (rule && !isActiveRule(rule) && draft.isActive);
    const blockedByLimit = wouldAddActive && (limitReached(limits) || (serverError && serverError.code === ERROR_LIMIT_REACHED));
    const maxActive = limits && typeof limits.max_active === 'number' ? limits.max_active : 50;

    // La simulación describe la frase de antes: una frase nueva se vuelve a simular, y la que estaba en
    // camino ya no se pinta.
    const forgetSimulation = () => {
        simulationRequest.current += 1;
        setSimulation(null);
        setSimulating(false);
    };

    const change = (next) => {
        setDraft(next);
        setServerError(null);
        forgetSimulation();
    };

    // «En tus palabras»: el borrador que armó la frase reemplaza al de la pantalla, con sus marcas. Cada
    // marca guarda cómo quedó su campo: vale mientras el campo siga así (`liveMarks`).
    const applyNaturalLanguage = (next, nextMarks) => {
        setDraft(next);
        setMarks(stampMarks(nextMarks, next));
        setServerError(null);
        forgetSimulation();
        setShowErrors(false);
    };

    const changeName = (value) => {
        setDraft((current) => ({...current, name: value, nameTouched: true}));
        if (serverError && serverError.field === 'name') setServerError(null);
    };

    // Una plantilla reemplaza la frase entera (el nombre vuelve a proponerse solo) y deja el foco en el valor.
    const applyTemplate = (template) => {
        const next = templateDraft(template);
        change(next);
        setTemplateBase(next);
        setPendingTemplate(null);
        setMarks({});
        setShowErrors(false);
        setFocusValue((count) => count + 1);
    };

    // Si el usuario ya armó algo (no es la frase vacía ni la de la última plantilla, sin tocar), se pregunta
    // antes de pisarlo: no hay deshacer.
    const draftHasWork = draft !== templateBase && JSON.stringify(draft) !== JSON.stringify(emptyDraft());
    const pickTemplate = (template) => {
        if (draftHasWork) setPendingTemplate(template);
        else applyTemplate(template);
    };

    // La frase de una plantilla, como se leería en el constructor, con «___» donde va el valor.
    const templateSentence = (template) => {
        const sample = templateDraft(template);
        const tokens = sentenceTokens(sample, {
            ...ctx, unit: unitOfMetric(sample.metric, options), scopeNames: [], hiddenCount: 0, recipientNames: [],
        });
        // El chip «Webhook» apagado no es parte de la frase: se lee «Avisar por Correo a mí…».
        return `${tokens.filter((token) => token.slot !== 'channelWebhook' || token.on)
            .map((token) => (token.slot === 'value' && token.empty ? '___' : token.text)).join(' ')}.`;
    };

    // La regla que se estaba editando ya no existe (o ya no se ve): se dice y se vuelve a la lista.
    const ruleIsGone = (error) => {
        if (error.code !== ERROR_NOT_FOUND || mode !== MODE_EDIT) return false;
        toast.error(t('alerts_center.rule_not_found'));
        if (onGone) onGone(rule ? rule.id : ruleId);
        return true;
    };

    const simulate = () => {
        const simErrors = validateDraft(draft, {locale, limits, allowNoChannels});
        if (Object.keys(simErrors).length) {
            setShowErrors(true);
            return;
        }
        setSimulating(true);
        setSimulation(null);
        simulationRequest.current += 1;
        const request = simulationRequest.current;
        // Una de Dashbo se simula por su id: el backend completa la métrica y el período, y toma el número, el
        // operador de una comparación y el alcance de la frase (ronda 5, R).
        const call = draft.systemLocked && rule
            ? AlertRulesApi.simulateValue(rule.id, parseValue(draft.value, locale), {
                operator: draft.conditionType === CONDITION_PERIOD_CHANGE ? draft.operator : null,
                scope: {type: draft.scope.type, ids: draft.scope.ids},
            })
            : AlertRulesApi.simulate(ruleInputFromDraft(draft, {includeName: false, locale}));
        call.then((response) => {
            // La frase cambió mientras se simulaba: esta respuesta (y su error) ya no describe nada.
            if (!mounted.current || request !== simulationRequest.current) return;
            setSimulating(false);
            if (!isOk(response) || !response.result) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.builder.simulate_error'});
                // Un error de un chip se dice en el chip y en la lista de arriba: no además abajo
                // (C-ac-fe-centro-04).
                if (error.field) setServerError(error);
                else setSimulation({error: error.key});
                return;
            }
            setSimulation(response.result);
        });
    };

    const save = () => {
        if (Object.keys(localErrors).length) {
            setShowErrors(true);
            return;
        }
        if (blockedByLimit || saving || nothingToSave || !editable) return;
        setSaving(true);
        const request = mode === MODE_EDIT
            ? AlertRulesApi.update(rule.id, update)
            : AlertRulesApi.create(ruleInputFromDraft(finalDraft, {locale}));
        request.then((response) => {
            if (!mounted.current) return;
            setSaving(false);
            const saved = isOk(response) && response.result ? response.result.rule : null;
            if (!saved) {
                const error = errorFromResponse(response);
                if (ruleIsGone(error)) return;
                setServerError(error);
                if (!error.field) toast.error(t(error.key, {max: maxActive}));
                return;
            }
            toast.success(t(mode === MODE_EDIT ? 'alerts_center.builder.updated_toast' : 'alerts_center.builder.saved_toast'));
            onSaved(saved, {created: mode !== MODE_EDIT});
        });
    };

    const dismissPause = () => {
        const next = new URLSearchParams(searchParams);
        next.delete(PARAM_PAUSE);
        setSearchParams(next, {replace: true});
    };

    const setActive = (next) => {
        if (!rule || acting || !canPauseRule) return;
        // El tope frena antes de pedir (C-ac-fe-centro-07).
        if (next && limitReached(limits)) {
            toast.error(t('alerts_center.builder.limit_reached', {max: maxActive}));
            return;
        }
        setActing(true);
        AlertRulesApi.update(rule.id, {is_active: next}).then((response) => {
            if (!mounted.current) return;
            setActing(false);
            const saved = isOk(response) && response.result ? response.result.rule : null;
            if (!saved) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.rules.toggle_error'});
                if (ruleIsGone(error)) return;
                toast.error(t(error.key, {max: maxActive}));
                return;
            }
            setRule(saved);
            setDraft((current) => ({...current, isActive: saved.is_active !== false}));
            toast.success(t(next ? 'alerts_center.rules.resumed_toast' : 'alerts_center.rules.paused_toast'));
            if (onRuleChanged) onRuleChanged(saved);
            if (askPause) dismissPause();
        });
    };

    const remove = () => {
        if (!rule || acting || !canDeleteRule) return;
        setActing(true);
        AlertRulesApi.remove(rule.id).then((response) => {
            if (!mounted.current) return;
            setActing(false);
            if (!isOk(response)) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.rules.delete_error'});
                if (!ruleIsGone(error)) toast.error(t(error.key));
                return;
            }
            onDeleted(rule);
        });
    };

    // «Dejar de recibirla» / «Volver a recibirla» (D19) desde el «⋯» del editor: la baja de quien mira.
    const setOptedOut = (next) => {
        if (!rule || acting) return;
        setActing(true);
        const request = next ? AlertRulesApi.optOut(rule.id) : AlertRulesApi.undoOptOut(rule.id);
        request.then((response) => {
            if (!mounted.current) return;
            setActing(false);
            const saved = isOk(response) ? ruleFromResponse(response) : null;
            if (!saved) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.row.opt_out_error'});
                if (!ruleIsGone(error)) toast.error(t(error.key));
                return;
            }
            const updated = {...withViewerOptOut(rule, next), ...saved};
            setRule(updated);
            // El borrador sigue a la baja: si no, el pie contaba un cambio fantasma y guardar la revertía.
            setDraft((current) => draftWithViewerExcluded(current, viewerId, next));
            if (onRuleChanged) onRuleChanged(updated);
        });
    };

    // «Volver al número de la agencia» (una excepción): se borra y sus líneas vuelven a la alerta de toda la agencia.
    const returnToAgency = () => {
        if (!rule || acting || !canDeleteRule) return;
        setActing(true);
        AlertRulesApi.remove(rule.id).then((response) => {
            if (!mounted.current) return;
            setActing(false);
            if (!isOk(response)) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.rules.delete_error'});
                if (!ruleIsGone(error)) toast.error(t(error.key));
                return;
            }
            onDeleted(rule, {returned: true});
        });
    };

    // ------------------------------------------------- cambios sin guardar y «Cancelar» (UD-18) -----
    // Editando, cuántas cosas cambiaron contra la regla guardada; creando, si la frase se apartó de la de arranque.
    const changes = mode === MODE_EDIT && rule ? draftChanges(rule, finalDraft, {locale}) : [];
    const dirty = mode === MODE_EDIT ? changes.length > 0 : (JSON.stringify(rawDraft) !== startDraft.current || wordsTyped);

    const leave = () => {
        setConfirming(false);
        if (onCancel) onCancel();
    };
    const requestCancel = () => {
        if (dirty && editable) setConfirming(true);
        else leave();
    };

    // La pregunta se enfoca al aparecer; al irse («Seguir editando» o Escape), el foco vuelve a «Cancelar»: si no, caía
    // al body y el próximo Escape ya no llegaba al editor (QA-ud18-22).
    const cancelRef = useRef(null);
    const wasConfirming = useRef(false);
    useEffect(() => {
        if (confirming) {
            wasConfirming.current = true;
            const button = confirmRef.current && confirmRef.current.querySelector('button');
            if (button) button.focus();
            return;
        }
        if (wasConfirming.current && cancelRef.current) cancelRef.current.focus();
        wasConfirming.current = false;
    }, [confirming]);

    // Escape hace lo mismo que «Cancelar». No cuando cierra algo abierto adentro: un chip, el combo de métricas o un
    // menú (esos lo manejan solos); con la pregunta en pantalla, Escape es «Seguir editando».
    const handleEscape = () => {
        if (confirming) {
            setConfirming(false);
            return;
        }
        requestCancel();
    };
    const onKeyDown = (event) => {
        if (event.key !== 'Escape' || event.defaultPrevented) return;
        const target = event.target;
        if (target && target.closest
            && target.closest('.alc-chip-menu, [class*="metric-combobox"], [aria-expanded="true"], [role="menu"]')) return;
        handleEscape();
    };

    // Y también con el foco en la página, sin haber hecho clic en un campo (QA-ud18-22): ese Escape va al body y no pasa
    // por el editor. Salvo que haya algo abierto que lo use (un menú, un panel, el desplegable del marco).
    const escapeRef = useRef(handleEscape);
    escapeRef.current = handleEscape;
    useEffect(() => {
        const onDocumentKey = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (event.target !== document.body && event.target !== document.documentElement) return;
            if (document.body.classList.contains('modal-open')) return;
            if (document.querySelector(PAGE_ESCAPE_OWNERS)) return;
            if (rootRef.current && rootRef.current.querySelector('[aria-expanded="true"]')) return;
            escapeRef.current();
        };
        document.addEventListener('keydown', onDocumentKey);
        return () => document.removeEventListener('keydown', onDocumentKey);
    }, []);

    // El «¿Pausar esta regla?» del correo se enfoca al aparecer: es un `alertdialog`.
    const pauseConfirmRef = useRef(null);
    const showPauseConfirm = askPause && rule && isActiveRule(rule) && canPauseRule;
    useEffect(() => {
        if (!showPauseConfirm || !pauseConfirmRef.current) return;
        const button = pauseConfirmRef.current.querySelector('button');
        if (button) button.focus();
    }, [showPauseConfirm]);

    // --------------------------------------------------------------------- dibujo ---------------

    if (sourceState === 'loading' || (optionsState === 'loading' && !options)) {
        return <div className="box-loader"><Spinner color="positive"/></div>;
    }
    if (sourceState === 'error') {
        return (
            <LoadErrorState title={t('alerts_center.builder.rule_load_error')}
                            text={t('alerts_center.load_error_explanation')}/>
        );
    }
    if (optionsState === 'error' && !options) {
        return (
            <LoadErrorState title={t('alerts_center.builder.options_error')}
                            text={t('alerts_center.load_error_explanation')}
                            retryLabel={t('alerts_center.retry')} onRetry={onRetryOptions}/>
        );
    }

    const errorList = [...new Set(Object.values(errors))];
    const active = rule ? isActiveRule(rule) : true;
    const creatorLeft = rule && rule.paused_reason === PAUSED_CREATOR_LEFT;
    const templates = mode === MODE_NEW && !duplicateId && canEdit && tab === TAB_STEPS
        ? availableTemplates(options) : [];
    const system = Boolean(rule && rule.kind === KIND_SYSTEM) || draft.systemLocked || draft.conditionType === CONDITION_CONTROL;
    const exception = isExceptionRule(rule);
    const badge = mode === MODE_EDIT && rule ? ruleBadge(rule, viewerId, t)
        : {text: t('alerts_center.mr.badge_mine'), mine: true};
    const teamAction = mode === MODE_EDIT && rule ? teamActionOf(rule) : null;
    const crumbs = mode === MODE_EDIT
        ? [t('alerts_center.title'), t('alerts_center.tabs.rules'), t('alerts_center.mr.crumb_edit')]
        : [t('alerts_center.title'), t('alerts_center.cta_new')];

    // El «⋯» del editor (UD-18): lo que se usa poco. «Eliminar» en las propias, «Volver a recibirla» / «Dejar de
    // recibirla» en las de equipo y «Volver al número de la agencia» en una excepción.
    const menu = [];
    if (teamAction) {
        menu.push({key: 'team', label: t(teamAction.actionKey), onSelect: () => setOptedOut(teamAction.next),
            icon: teamAction.next ? null : <ArrowUUpLeft size={15}/>});
    }
    if (exception && canDeleteRule) {
        menu.push({key: 'return', label: t('alerts_center.row.return_to_agency'), onSelect: returnToAgency, icon: <Rows size={15}/>});
    }
    if (rule && rule.kind !== KIND_SYSTEM && canDeleteRule) {
        menu.push({key: 'delete', label: t('alerts_center.rules.action_delete'), onSelect: remove, danger: true,
            icon: <Trash size={15}/>, separator: menu.length > 0});
    }

    const renderName = () => {
        // Una de Dashbo (o un aviso de control) se titula por su clave: el nombre no se cambia.
        if (system && rule) {
            return (
                <div className="alc-ed-name__text">
                    <h2 className="alc-ed-title" data-testid="alc-ed-title">{alertTitle(rule, t)}</h2>
                    {exception && <span className="alc-ed-name__sub">{exceptionTitle(rule, t)}</span>}
                </div>
            );
        }
        return (
            <label className="alc-ed-name__field">
                {/* El ancho sigue al nombre (`size`, en caracteres): un ancho en % no se resuelve dentro de un título que se
                    ajusta a su contenido, y el nombre se cortaba. */}
                <input id="alc-rule-name" type="text" className="alc-ed-title-input" maxLength={NAME_MAX_LENGTH + 20}
                       size={Math.min(NAME_MAX_LENGTH, Math.max(24, (effectiveName || '').length + 3))}
                       value={effectiveName} placeholder={t('alerts_center.mr.name_placeholder')}
                       title={effectiveName || undefined}
                       aria-label={t('alerts_center.builder.name_label')} aria-invalid={errors.name ? 'true' : 'false'}
                       readOnly={!editable} onChange={(event) => changeName(event.target.value)}/>
                {editable && <PencilSimple size={16} className="alc-ed-name__pen" aria-hidden="true"/>}
            </label>
        );
    };

    const renderFoot = () => {
        if (confirming) {
            return (
                <div className="alc-ed-confirm" role="alertdialog" aria-labelledby="alc-discard-title"
                     data-testid="alc-discard-confirm" ref={confirmRef}>
                    <p id="alc-discard-title" className="alc-ed-confirm__text">
                        <strong>{t('alerts_center.mr.discard_title')}</strong>{' '}
                        <span>{mode === MODE_EDIT
                            ? t('alerts_center.mr.discard_text', {count: changes.length})
                            : t('alerts_center.mr.discard_text_new')}</span>
                    </p>
                    <div className="alc-ed-confirm__acts">
                        <button type="button" className="alc-btn -ghost" onClick={() => setConfirming(false)}
                                data-testid="alc-keep-editing">
                            {t('alerts_center.mr.keep_editing')}
                        </button>
                        <button type="button" className="alc-btn -ghost -danger" onClick={leave} data-testid="alc-discard">
                            {t('alerts_center.mr.discard_and_back')}
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <div className="alc-ed-foot" data-testid="alc-ed-foot">
                <div className="alc-ed-foot__side">
                    <button type="button" ref={cancelRef} className="alc-btn -quiet" onClick={requestCancel}
                            data-testid="alc-cancel">
                        {t('alerts_center.mr.cancel')}
                    </button>
                </div>
                <div className="alc-ed-foot__side">
                    {mode === MODE_EDIT && editable && (
                        <span className={`alc-dirty${dirty ? ' -on' : ''}`} data-testid="alc-dirty">
                            {dirty ? t('alerts_center.mr.unsaved', {count: changes.length}) : t('alerts_center.mr.no_changes')}
                        </span>
                    )}
                    {/* Un aviso de control no tiene número que simular. */}
                    {draft.conditionType !== CONDITION_CONTROL && (
                        <button type="button" className="alc-btn -ghost" disabled={simulating} onClick={simulate}
                                data-testid="alc-simulate">
                            <Play size={13} weight="bold" aria-hidden="true"/>
                            {simulating ? t('alerts_center.builder.simulating') : t('alerts_center.builder.simulate')}
                        </button>
                    )}
                    <button type="button" className="alc-btn -primary"
                            disabled={saving || blockedByLimit || nothingToSave || !editable} onClick={save}
                            data-testid="alc-save">
                        {saving ? t('alerts_center.saving')
                            : t(mode === MODE_EDIT ? 'alerts_center.builder.save_changes' : 'alerts_center.builder.save')}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="alc-builder alc-ed" data-testid="alc-builder" data-mode={mode} ref={rootRef} onKeyDown={onKeyDown}>
            <DetailTop crumbs={crumbs} onBack={requestCancel}/>

            <div className="alc-ed-head">
                <div className="alc-ed-name">
                    {renderName()}
                    <span className={`alc-badge${badge.mine ? ' -mine' : ''}`} data-testid="alc-ed-badge">{badge.text}</span>
                    {mode === MODE_EDIT && rule && (
                        <span className={`alc-status${active ? ' -on' : ' -off'}`} data-testid="alc-ed-status">
                            {active ? t('alerts_center.row.state_active') : t('alerts_center.row.state_paused')}
                        </span>
                    )}
                </div>
                {mode === MODE_EDIT && rule && canEdit && (
                    <div className="alc-ed-acts">
                        {!creatorLeft && canPauseRule && (
                            <button type="button" className="alc-btn -ghost" disabled={acting} onClick={() => setActive(!active)}
                                    data-testid="alc-ed-pause">
                                {active ? <Pause size={13} weight="bold"/> : <Play size={13} weight="bold"/>}
                                {active ? t('alerts_center.rules.action_pause') : t('alerts_center.rules.action_resume')}
                            </button>
                        )}
                        {/* Una copia propia de una de Dashbo serían dos correos por lo mismo (ronda 5). */}
                        {rule.kind !== KIND_SYSTEM && (
                            <button type="button" className="alc-btn -ghost" disabled={acting} onClick={() => onDuplicate(rule)}>
                                <Copy size={13} weight="bold"/>
                                {t('alerts_center.rules.action_duplicate')}
                            </button>
                        )}
                        <RowMenu items={acting ? menu.map((item) => ({...item, disabled: true})) : menu}
                                 label={t('alerts_center.mr.more_actions')} testId="alc-ed-more"/>
                    </div>
                )}
            </div>
            {!draft.nameTouched && nameFollowsSentence && suggestedName && !system && (
                <p className="alc-hint alc-ed-name__hint">{t('alerts_center.builder.name_auto')}</p>
            )}
            {creatorLeft && <p className="alc-hint">{t('alerts_center.rules.paused_creator_left')}</p>}
            {rule && rule.paused_reason === PAUSED_LIMIT && <p className="alc-hint">{t('alerts_center.rules.paused_limit')}</p>}

            {showPauseConfirm && (
                <div className="alc-confirm" role="alertdialog" aria-labelledby="alc-pause-title" data-testid="alc-pause-confirm"
                     ref={pauseConfirmRef}>
                    <p id="alc-pause-title" className="alc-confirm__text">{t('alerts_center.builder.pause_confirm')}</p>
                    <div className="alc-confirm__acts">
                        <button type="button" className="alc-btn -primary" disabled={acting} onClick={() => setActive(false)}>
                            {t('alerts_center.rules.action_pause')}
                        </button>
                        <button type="button" className="alc-btn -ghost" onClick={dismissPause}>
                            {t('alerts_center.builder.pause_keep')}
                        </button>
                    </div>
                </div>
            )}

            {!editable && <p className="alc-notice -warn" role="note" data-testid="alc-read-only">{t('alerts_center.no_edit_permission')}</p>}

            {mode === MODE_NEW && (
                <div className="uiseg alc-builder__modes" role="tablist" aria-label={t('alerts_center.builder.modes_label')}>
                    {[TAB_STEPS, TAB_TEXT].map((key) => (
                        <button key={key} type="button" role="tab" aria-selected={tab === key}
                                className={`uiseg__btn${tab === key ? ' -active' : ''}`} onClick={() => setTab(key)}>
                            {t(key === TAB_STEPS ? 'alerts_center.builder.mode_steps' : 'alerts_center.builder.mode_text')}
                        </button>
                    ))}
                </div>
            )}

            {/* Oculto y no desmontado: volver de «Paso a paso» encuentra la frase y lo que se entendió. La
                `key` es el borrador de base: pasar de duplicar a «Crear una alerta» tira la frase con él. */}
            {mode === MODE_NEW && (
                <div hidden={tab !== TAB_TEXT} data-testid="alc-nl-panel">
                    <NaturalLanguageDraft key={sourceId || 'new'} hasModule={hasNaturalLanguage} canEdit={editable}
                                          marks={shownMarks}
                                          onDraft={applyNaturalLanguage} onManual={() => setTab(TAB_STEPS)}
                                          onTextChange={(value) => setWordsTyped(value.trim() !== '')}
                                          onChooseScope={(optionId) => change(chooseScopeOption(draft, optionId))}/>
                </div>
            )}

            {templates.length > 0 && (
                <section className="alc-card alc-templates" aria-labelledby="alc-templates-title" data-testid="alc-templates">
                    <h2 id="alc-templates-title" className="alc-card__title">{t('alerts_center.builder.templates_title')}</h2>
                    <p className="alc-card__lede">{t('alerts_center.builder.templates_text')}</p>
                    <ul className="alc-templates__list">
                        {templates.map((template) => (
                            <li key={template.key}>
                                <button type="button" className="alc-template" data-template={template.key}
                                        onClick={() => pickTemplate(template)}>
                                    {templateSentence(template)}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {pendingTemplate && (
                        <div className="alc-confirm alc-templates__confirm" role="alertdialog"
                             aria-labelledby="alc-template-confirm-title" data-testid="alc-template-confirm"
                             ref={templateConfirmRef}>
                            <p id="alc-template-confirm-title" className="alc-confirm__text">
                                {t('alerts_center.builder.template_replace_confirm')}
                            </p>
                            <div className="alc-confirm__acts">
                                <button type="button" className="alc-btn -primary" onClick={() => applyTemplate(pendingTemplate)}>
                                    {t('alerts_center.builder.template_replace')}
                                </button>
                                <button type="button" className="alc-btn -ghost" onClick={() => setPendingTemplate(null)}>
                                    {t('alerts_center.builder.template_keep')}
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            )}

            <section className="alc-ed-card alc-builder__card" aria-label={t('alerts_center.builder.sentence_label')}>
                <div className="alc-ed-card__body">
                    <SentenceChips tokens={tokens} draft={draft} options={options} unit={unit} errors={errors}
                                   limits={limits} scopeLocked={hiddenCount > 0} staleScopeIds={staleScope}
                                   marks={shownMarks} allowNoChannels={allowNoChannels} onlyMe={lockRecipients} onChange={change}/>
                    <div className="alc-ed-helper" data-testid="alc-ed-helper">
                        {emailForced && (
                            <span className="alc-ed-helper__item">
                                <Lock size={14} aria-hidden="true"/>{t('alerts_center.mr.helper_email')}
                            </span>
                        )}
                        {webhookOffered && (
                            <span className="alc-ed-helper__item" data-testid="alc-webhook-helper">
                                <WebhooksLogo size={14} aria-hidden="true"/>
                                {t(webhookReady ? 'alerts_center.mr.helper_webhook' : 'alerts_center.builder.webhook_also_unconfigured')}
                            </span>
                        )}
                        {/* La moneda es información, no una advertencia: en gris, con ⓘ (UD-18). */}
                        {needsCurrencyNote(draft.scope, unit, options) && (
                            <span className="alc-ed-helper__item -currency">
                                <Info size={14} aria-hidden="true"/>{t('alerts_center.builder.currency_note')}
                            </span>
                        )}
                    </div>
                    {lockRecipients && <p className="alc-hint" data-testid="alc-only-me">{t('alerts_center.builder.only_me_hint')}</p>}
                    {errorList.length > 0 && (
                        <ul className="alc-builder__errors" role="alert" data-testid="alc-builder-errors">
                            {errorList.map((key) => <li key={key}>{t(key, {max: maxActive})}</li>)}
                        </ul>
                    )}
                    {hiddenCount > 0 && (
                        <p className="alc-hint" data-testid="alc-scope-locked">
                            {t('alerts_center.builder.scope_locked', {count: hiddenCount})}
                        </p>
                    )}
                    {staleScope.length > 0 && hiddenCount === 0 && (
                        <p className="alc-notice -warn" data-testid="alc-scope-stale">
                            {t('alerts_center.builder.scope_stale', {count: staleScope.length})}
                        </p>
                    )}
                    {staleRecipients.length > 0 && (
                        <p className="alc-notice -warn" data-testid="alc-recipients-stale">
                            {t('alerts_center.builder.recipients_stale', {count: staleRecipients.length})}
                        </p>
                    )}
                    {blockedByLimit && (
                        <p className="alc-notice -warn" data-testid="alc-limit">{t('alerts_center.builder.limit_reached', {max: maxActive})}</p>
                    )}
                    {simulation && simulation.error && (
                        <p className="alc-notice -warn" role="status">{t(simulation.error)}</p>
                    )}
                    {simulation && !simulation.error && <SimulationResult simulation={simulation} unit={unit} metric={draft.metric}/>}
                </div>
                {renderFoot()}
            </section>
        </div>
    );
}
